import {
  LoanApplication,
  LOSPlatform,
  useLoanDashboard,
  FacilityTab,
} from "../../../contexts";
import { FC, ReactNode, useCallback, useMemo } from "react";
import {
  AdditionalInfoType,
  LoanApplicationCaseStatusLabelsForCreditLine,
  LoanApplicationCaseStatusLabels,
  LoanApplicationStatus,
  LoanApplicationStatusLabels,
  LoanProductType,
} from "../../../hooks/api/types";
import { Card } from "../../../components";
import LoanCardActions from "./LoanCardActions";
import LoanDetails from "./LoanDetails";
import LoanDetailsCreditLine from "./LoanDetailsCreditLine";
import { clsx } from "clsx";
import { useDpConfig } from "../../../contexts/dpConfig";
import { getLoanDateDisplay } from "../LineOfCredit/service";

type LoanCardProps = {
  loan: LoanApplication;
  index: number;
  title?: string | ReactNode;
  showTitle?: boolean;
};

export const LoanCard: FC<LoanCardProps> = ({
  loan,
  index,
  title,
  showTitle = true,
}) => {
  const {
    state: { products },
  } = useDpConfig();

  const {
    actions: { update },
  } = useLoanDashboard();

  const dateDisplay = useMemo(() => getLoanDateDisplay(loan), [loan]);

  const isCreditFacility =
    loan.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
    loan.loanType === LoanProductType.LOC_PAYABLE_FINANCING;

  const handleOpenFacilityOverview = useCallback(async () => {
    if (isCreditFacility) {
      await update({
        currentLoanApplicationId: loan.id,
        currentLoanApplication: loan,
        showFacilityOverview: true,
        showPaymentSchedule: false,
        showPaymentHistory: false,
        showTransactionHistory: false,
        showDrawdownForm: false,
        showAdditionalInfoRequired: false,
        facilityActiveTab: FacilityTab.OVERVIEW,
      });
    }
  }, [isCreditFacility, loan, update]);

  const readableStatus = useMemo(() => {
    if (
      loan.additionalInfo?.length > 0 &&
      loan.additionalInfo?.every(
        (info: any) => info.type === AdditionalInfoType.SHAREHOLDERS_AECB
      )
    ) {
      return LoanApplicationStatusLabels[LoanApplicationStatus.PREAPPROVED];
    } else {
      if (
        loan.losPlatform === LOSPlatform.CREDIBLEX &&
        loan.status === LoanApplicationStatus.SUBMITTED &&
        loan.caseStatus
      ) {
        return isCreditFacility
          ? LoanApplicationCaseStatusLabelsForCreditLine[loan.caseStatus]
          : LoanApplicationCaseStatusLabels[loan.caseStatus];
      } else {
        return LoanApplicationStatusLabels[loan.status];
      }
    }
  }, [loan, isCreditFacility]);
  const productConfig = products?.find(
    (product) => product.id === loan.loanProductId
  );

  const drawdownError =
    isCreditFacility &&
    loan.drawdownEligibility &&
    !loan.drawdownEligibility.isValid
      ? loan.drawdownEligibility.reason ?? null
      : null;

  const handleApplyDrawDown = useCallback(async () => {
    if (
      loan.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
      loan.loanType === LoanProductType.LOC_PAYABLE_FINANCING
    ) {
      if (loan.status === LoanApplicationStatus.ADDITIONAL_INFO_REQUESTED) {
        update({
          currentLoanApplicationId: loan.id,
          currentLoanApplication: loan,
          showAdditionalInfoRequired: true,
        });
        return;
      }

      update({
        currentLoanApplicationId: loan.id,
        currentLoanApplication: loan,
        showDrawdownForm: true,
      });
    }
  }, [update, loan]);

  const computedTitle = useMemo(() => {
    if (!showTitle) return undefined;
    const baseTitle = title ?? "Your loans";
    return index ? undefined : baseTitle;
  }, [index, title, showTitle]);

  return (
    <Card
      key={`loan-${index}`}
      title={computedTitle}
      hoverEffect={isCreditFacility}
      hoverEffectColor="#0043CE"
    >
      <div
        onClick={handleOpenFacilityOverview}
        className={isCreditFacility ? "cx-cursor-pointer" : "cx-cursor-default"}
      >
        <div className="cx-w-full sm:cx-flex cx-flex-row cx-items-center cx-px-6 cx-pb-4 cx-border-b-0.5 cx-border-neutral-light">
          <div className="cx-grow cx-flex cx-flex-row cx-items-center">
            <div className="cx-flex cx-flex-col cx-gap-y-1 cx-mr-4">
              {isCreditFacility && (
                <div className="cx-inline-flex cx-items-center cx-gap-x-1 cx-text-xs cx-text-text-secondary">
                  <span className="cx-inline-block cx-w-2 cx-h-2 cx-rounded-full cx-bg-teal-500" />
                  <span>Line of credit</span>
                </div>
              )}
              <div className="cx-flex cx-items-center cx-gap-3">
                <div className="cx-text-text-primary cx-font-semibold cx-text-lg">
                  {productConfig?.config?.customProductAlias ||
                    productConfig?.displayName ||
                    loan?.loanProduct?.displayName ||
                    "-"}
                </div>
                <div
                  className={clsx(
                    "cx-inline-flex cx-items-center cx-rounded-full cx-px-3 cx-py-1.5 cx-text-xs cx-font-medium",
                    readableStatus?.displayClasses || ""
                  )}
                >
                  {readableStatus?.label || "-"}
                </div>
              </div>
            </div>
          </div>
          <div className="cx-flex cx-flex-col cx-items-end cx-gap-y-1">
            {loan.company?.name && (
              <div className="cx-inline-flex cx-items-center cx-gap-x-1">
                <span
                  className="cx-inline-block cx-w-1.5 cx-h-1.5 cx-rounded-full"
                  style={{ backgroundColor: "#F1C21B" }}
                />
                <span className=" cx-text-center cx-text-xs cx-font-medium">
                  {loan.company.name}
                </span>
              </div>
            )}
            <div className="sm:cx-flex cx-text-text-tertiary cx-flex-row cx-items-center cx-gap-x-2">
              <div
                className={`cx-font-medium cx-text-sm  cx-text-text-tertiary ${
                  productConfig?.config?.enableVendorScreen
                    ? "cx-cursor-pointer"
                    : ""
                }`}
                onClick={async () => {
                  if (productConfig?.config?.enableVendorScreen) {
                    await update({
                      currentLoanApplicationId: loan.id,
                      currentLoanApplication: loan,
                      showFacilityOverview: true,
                      facilityActiveTab: FacilityTab.OVERVIEW,
                    });
                  }
                }}
              >
                {`ID #${loan.externalId}`}
              </div>
              <div className="cx-hidden sm:cx-block cx-w-1 cx-h-1 cx-rounded-btn cx-bg-neutral-light" />

              {!isCreditFacility && loan.interestRate && (
                <div className="sm:cx-flex cx-flex-row cx-items-center cx-gap-x-2">
                  <div className="cx-text-text-secondary cx-font-medium cx-text-sm">
                    {`${loan.interestRate} % Interest`}
                  </div>
                  <div className="cx-hidden sm:cx-block cx-w-1 cx-h-1 cx-rounded-btn cx-bg-neutral-light" />
                </div>
              )}

              {dateDisplay && (
                <div
                  className={clsx(
                    "cx-font-medium cx-text-sm",
                    dateDisplay.startsWith("Due date")
                      ? "cx-text-[#A2191F]"
                      : "cx-text-text-tertiary"
                  )}
                >
                  {dateDisplay}
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={`cx-transition-colors ${
            isCreditFacility ? "hover:cx-bg-neutral-50" : ""
          }`}
        >
          {isCreditFacility ? (
            <LoanDetailsCreditLine loan={loan} />
          ) : (
            <LoanDetails loan={loan} />
          )}
        </div>
      </div>
      <LoanCardActions
        loan={loan}
        onApplyDrawdown={handleApplyDrawDown}
        drawdownError={drawdownError}
      />
    </Card>
  );
};

export default LoanCard;
